import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { MapPin, X, ChevronRight, Loader2, Users, Clock, CreditCard, User } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * UnifiedTableSelector - Composant unifié de sélection de tables
 * 
 * Utilisé partout dans l'application pour:
 * - Sélection de table dans le POS
 * - Affichage des tables assignées pour les serveurs
 * - Vue d'ensemble des tables pour les managers
 * 
 * Respecte les permissions par rôle:
 * - Admin/Directeur: voient toutes les tables
 * - Serveur: voit uniquement ses tables assignées
 */

// Configuration des statuts avec couleurs uniformes
const STATUS_CONFIG = {
  libre: {
    label: 'Libre',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-400',
    textColor: 'text-emerald-700',
    badgeBg: 'bg-emerald-500',
    badgeText: 'text-white',
    hoverBg: 'hover:bg-emerald-100',
    hoverBorder: 'hover:border-emerald-500'
  },
  free: {
    label: 'Libre',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-400',
    textColor: 'text-emerald-700',
    badgeBg: 'bg-emerald-500',
    badgeText: 'text-white',
    hoverBg: 'hover:bg-emerald-100',
    hoverBorder: 'hover:border-emerald-500'
  },
  available: {
    label: 'Libre',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-400',
    textColor: 'text-emerald-700',
    badgeBg: 'bg-emerald-500',
    badgeText: 'text-white',
    hoverBg: 'hover:bg-emerald-100',
    hoverBorder: 'hover:border-emerald-500'
  },
  brouillon: {
    label: 'Brouillon',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-400',
    textColor: 'text-amber-700',
    badgeBg: 'bg-amber-500',
    badgeText: 'text-white',
    hoverBg: 'hover:bg-amber-100',
    hoverBorder: 'hover:border-amber-500'
  },
  occupee: {
    label: 'Occupée',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-400',
    textColor: 'text-orange-700',
    badgeBg: 'bg-orange-500',
    badgeText: 'text-white',
    hoverBg: 'hover:bg-orange-100',
    hoverBorder: 'hover:border-orange-500'
  },
  occupied: {
    label: 'Occupée',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-400',
    textColor: 'text-orange-700',
    badgeBg: 'bg-orange-500',
    badgeText: 'text-white',
    hoverBg: 'hover:bg-orange-100',
    hoverBorder: 'hover:border-orange-500'
  },
  en_cours_de_paiement: {
    label: 'En paiement',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-500',
    textColor: 'text-blue-700',
    badgeBg: 'bg-blue-600',
    badgeText: 'text-white',
    hoverBg: 'hover:bg-blue-100',
    hoverBorder: 'hover:border-blue-600'
  },
  a_debarrasser: {
    label: 'À débarrasser',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-400',
    textColor: 'text-purple-700',
    badgeBg: 'bg-purple-500',
    badgeText: 'text-white',
    hoverBg: 'hover:bg-purple-100',
    hoverBorder: 'hover:border-purple-500'
  },
  reservee: {
    label: 'Réservée',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-400',
    textColor: 'text-indigo-700',
    badgeBg: 'bg-indigo-500',
    badgeText: 'text-white',
    hoverBg: 'hover:bg-indigo-100',
    hoverBorder: 'hover:border-indigo-500'
  }
};

// Format price
const formatPrice = (price) => {
  if (!price && price !== 0) return '0 F';
  return `${Math.round(price).toLocaleString('fr-FR')} F`;
};

// Format time elapsed
const formatTimeElapsed = (timestamp) => {
  if (!timestamp) return null;
  const diff = Date.now() - new Date(timestamp).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return '< 1 min';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h${minutes % 60}`;
};

/**
 * UnifiedTableCard - Carte de table moderne et uniforme
 */
const UnifiedTableCard = ({
  table,
  onClick,
  isSelected = false,
  disabled = false,
  showDetails = true,
  size = 'normal', // 'compact' | 'normal' | 'large'
  currentUser
}) => {
  const status = table?.status || 'libre';
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.libre;
  const [prevStatus, setPrevStatus] = React.useState(status);
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  
  // Detect status change and trigger animation
  React.useEffect(() => {
    if (prevStatus !== status) {
      setIsTransitioning(true);
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        setPrevStatus(status);
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [status, prevStatus]);
  
  // Calculer le total UNIQUEMENT à partir des items (pas des données résiduelles)
  const hasItems = (table?.sent_items?.length > 0) || (table?.draft_items?.length > 0);
  const calculatedTotal = 
    (table?.sent_items?.reduce((sum, item) => sum + (item.line_total || 0), 0) || 0) +
    (table?.draft_items?.reduce((sum, item) => sum + (item.line_total || 0), 0) || 0);
  
  // N'afficher le total que si la table est occupée ET a des items
  const total = (status !== 'libre' && hasItems) ? (calculatedTotal || table?.total || 0) : 0;
  
  const timeElapsed = (status !== 'libre') ? formatTimeElapsed(table?.opened_at) : null;
  const covers = table?.covers || table?.guests || 0;
  
  // Tout le monde peut voir le total des tables occupées
  const canSeeTotal = status !== 'libre' && total > 0;

  // Classes responsive selon la taille
  const sizeClasses = {
    compact: 'p-2 min-h-[60px]',
    normal: 'p-3 min-h-[80px] sm:min-h-[100px]',
    large: 'p-4 min-h-[100px] sm:min-h-[120px]'
  };

  return (
    <button
      onClick={() => !disabled && onClick?.(table)}
      disabled={disabled}
      data-testid={`unified-table-card-${table?.number}`}
      className={cn(
        // Base styles
        'relative w-full rounded-xl border-2',
        'flex flex-col items-center justify-center text-center',
        'active:scale-[0.98] touch-manipulation',
        // Smooth transitions for all properties
        'transition-all duration-300 ease-in-out',
        sizeClasses[size],
        // Status colors
        config.bgColor,
        config.borderColor,
        config.textColor,
        config.hoverBg,
        config.hoverBorder,
        // Selection state
        isSelected && 'ring-2 ring-offset-2 ring-blue-500 border-blue-500',
        // Disabled state
        disabled && 'opacity-50 cursor-not-allowed',
        // Status transition animation
        isTransitioning && 'animate-pulse scale-105 shadow-lg'
      )}
      style={{
        // Custom animation for status change
        animation: isTransitioning ? 'tableStatusChange 0.6s ease-in-out' : undefined
      }}
    >
      {/* Badge de statut */}
      <div className={cn(
        'absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide',
        'transition-all duration-300 ease-in-out',
        config.badgeBg,
        config.badgeText,
        isTransitioning && 'scale-110'
      )}>
        {config.label}
      </div>

      {/* Numéro de table */}
      <span className={cn(
        'font-bold transition-transform duration-300',
        size === 'compact' ? 'text-xl' : 'text-2xl sm:text-3xl',
        config.textColor,
        isTransitioning && 'scale-110'
      )}>
        {table?.number || '?'}
      </span>

      {/* Détails (si showDetails) */}
      {showDetails && (
        <div className="mt-1 space-y-0.5 w-full transition-opacity duration-300">
          {/* Nom du serveur/occupant - TOUJOURS visible pour tables occupées (tout le monde peut voir) */}
          {status !== 'libre' && (table?.waiter_name || table?.server_name || table?.assigned_server) && (
            <div className="flex items-center justify-center gap-1 text-[10px] font-medium truncate px-1">
              <User className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{table?.waiter_name || table?.server_name || table?.assigned_server}</span>
            </div>
          )}
          
          {/* Total - visible selon permissions */}
          {size !== 'compact' && canSeeTotal && total > 0 && (
            <div className="flex items-center justify-center gap-1 text-xs font-semibold">
              <CreditCard className="w-3 h-3" />
              {formatPrice(total)}
            </div>
          )}
          
          {/* Infos secondaires - temps toujours visible pour tables occupées */}
          <div className="flex items-center justify-center gap-2 text-[10px] opacity-70">
            {size !== 'compact' && covers > 0 && (
              <span className="flex items-center gap-0.5">
                <Users className="w-3 h-3" />
                {covers}
              </span>
            )}
            {timeElapsed && (
              <span className="flex items-center gap-0.5">
                <Clock className="w-3 h-3" />
                {timeElapsed}
              </span>
            )}
          </div>
        </div>
      )}
      
      {/* Ripple effect on status change */}
      {isTransitioning && (
        <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none">
          <div className="absolute inset-0 bg-white/30 animate-ping" style={{ animationDuration: '0.5s' }} />
        </div>
      )}
    </button>
  );
};

/**
 * UnifiedTableSelector - Composant principal de sélection
 */
const UnifiedTableSelector = ({
  open,
  onClose,
  onSelect,
  tables = [],
  loading = false,
  title = 'Sélectionner une table',
  filterFreeOnly = false,
  showAllStatuses = true,
  currentUser,
  selectedTableId = null
}) => {
  const { user } = useAuth();
  const activeUser = currentUser || user;

  // Filtrer les tables selon les permissions
  const filteredTables = React.useMemo(() => {
    let result = [...tables];
    
    // Filtrer par statut si demandé
    if (filterFreeOnly) {
      result = result.filter(t => t.status === 'libre' || t.status === 'free' || t.status === 'available');
    }
    
    // Filtrer par assignation pour les serveurs
    const isServer = ['SERVEUR', 'SERVEUSE', 'MAITRE_HOTEL'].includes(activeUser?.role);
    const isManager = ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'].includes(activeUser?.role);
    
    if (isServer && !isManager) {
      result = result.filter(t => 
        t.status === 'libre' || 
        t.status === 'free' ||
        t.waiter_id === activeUser?.id ||
        t.assigned_to_id === activeUser?.id ||
        t.server_id === activeUser?.id
      );
    }
    
    // Trier par numéro
    return result.sort((a, b) => (a.number || 0) - (b.number || 0));
  }, [tables, filterFreeOnly, activeUser]);

  // Grouper par zone
  const tablesByZone = React.useMemo(() => {
    const groups = {};
    filteredTables.forEach(table => {
      const zone = table.zone || 'Salle';
      if (!groups[zone]) groups[zone] = [];
      groups[zone].push(table);
    });
    return groups;
  }, [filteredTables]);

  // Stats
  const stats = React.useMemo(() => ({
    libre: filteredTables.filter(t => t.status === 'libre' || t.status === 'free' || t.status === 'available').length,
    occupee: filteredTables.filter(t => t.status === 'occupee' || t.status === 'occupied').length,
    brouillon: filteredTables.filter(t => t.status === 'brouillon').length,
    enPaiement: filteredTables.filter(t => t.status === 'en_cours_de_paiement').length,
    aDebarrasser: filteredTables.filter(t => t.status === 'a_debarrasser').length,
    total: filteredTables.length
  }), [filteredTables]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] p-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="p-4 pb-2 border-b bg-gradient-to-r from-slate-50 to-gray-50">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 text-lg font-bold">
              <MapPin className="w-5 h-5 text-blue-600" />
              {title}
            </DialogTitle>
          </div>
          
          {/* Stats Bar */}
          {showAllStatuses && (
            <div className="flex items-center gap-2 mt-2 flex-wrap text-xs">
              <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                {stats.libre} Libres
              </span>
              {stats.brouillon > 0 && (
                <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-700 font-medium">
                  {stats.brouillon} Brouillon
                </span>
              )}
              {stats.occupee > 0 && (
                <span className="px-2 py-1 rounded-full bg-orange-100 text-orange-700 font-medium">
                  {stats.occupee} Occupées
                </span>
              )}
              {stats.enPaiement > 0 && (
                <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-700 font-medium">
                  {stats.enPaiement} En paiement
                </span>
              )}
              {stats.aDebarrasser > 0 && (
                <span className="px-2 py-1 rounded-full bg-purple-100 text-purple-700 font-medium">
                  {stats.aDebarrasser} À débarrasser
                </span>
              )}
            </div>
          )}
        </DialogHeader>

        {/* Content */}
        <ScrollArea className="flex-1 max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : filteredTables.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <MapPin className="w-12 h-12 mb-3 opacity-30" />
              <p className="font-medium">Aucune table disponible</p>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {Object.entries(tablesByZone).map(([zone, zoneTables]) => (
                <div key={zone}>
                  <h3 className="text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                    <ChevronRight className="w-4 h-4" />
                    {zone} ({zoneTables.length})
                  </h3>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-3">
                    {zoneTables.map(table => (
                      <UnifiedTableCard
                        key={table.id}
                        table={table}
                        onClick={onSelect}
                        isSelected={selectedTableId === table.id}
                        disabled={filterFreeOnly && table.status !== 'libre' && table.status !== 'free' && table.status !== 'available'}
                        showDetails={!filterFreeOnly}
                        size="normal"
                        currentUser={activeUser}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="p-3 border-t bg-gray-50 flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Fermer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * QuickTableDropdown - Dropdown rapide pour le POS (version inline)
 */
export const QuickTableDropdown = ({
  tables = [],
  onSelect,
  onViewAll,
  loading = false,
  className
}) => {
  const { user } = useAuth();
  
  const freeTables = tables
    .filter(t => t.status === 'libre' || t.status === 'free' || t.status === 'available')
    .sort((a, b) => (a.number || 0) - (b.number || 0));

  return (
    <div className={cn(
      'absolute top-full left-0 right-0 mt-1 bg-white border rounded-xl shadow-xl z-50',
      'max-h-72 overflow-hidden',
      className
    )}>
      {/* Header */}
      <div className="p-2 border-b bg-gradient-to-r from-emerald-50 to-green-50">
        <span className="text-xs font-semibold text-emerald-700 flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          Tables libres ({freeTables.length})
        </span>
      </div>

      {/* Content */}
      <ScrollArea className="max-h-48">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
          </div>
        ) : freeTables.length === 0 ? (
          <div className="p-4 text-center text-gray-500 text-sm">
            Aucune table libre
          </div>
        ) : (
          <div className="grid grid-cols-4 sm:grid-cols-5 gap-1.5 p-2">
            {freeTables.map(table => (
              <button
                key={table.id}
                onClick={() => onSelect(table)}
                data-testid={`quick-table-${table.number}`}
                className={cn(
                  'p-2 text-center rounded-lg transition-all duration-150',
                  'bg-emerald-50 hover:bg-emerald-100',
                  'border border-emerald-200 hover:border-emerald-400',
                  'active:scale-95 touch-manipulation'
                )}
              >
                <span className="font-bold text-emerald-700 text-sm">{table.number}</span>
              </button>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Footer */}
      {onViewAll && (
        <div className="p-2 border-t bg-gray-50">
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full text-xs h-8"
            onClick={onViewAll}
          >
            <MapPin className="w-3 h-3 mr-1" />
            Voir le plan complet
          </Button>
        </div>
      )}
    </div>
  );
};

// Export both card and selector
export { UnifiedTableCard };
export default UnifiedTableSelector;
